import { DiscoveryPromotion } from '@teko/discovery';
import { t } from '../i18n';
import {
  IActiveCombo,
  IComboGroup,
  IProductPrice,
  ISelectingProduct,
  IPromotionBenefit,
  IComboProduct,
} from 'types';
import { formatCurrency } from '@teko/teko-ecommerce';
import { CartItem, CartItemGroup } from '@teko/cart/v2';
import { NumberDisplayFormat } from '@teko/types/ecommerce';

interface ComboPromotionView {
  hasImage: boolean;
  image?: string;
  icon?: string;
  tag?: string;
  discount?: number;
  title?: string;
  description?: string;
}

interface Item {
  icon: string;
  image: string | null;
  name: string | null;
  discount: number | null;
}

type ICombo = Omit<
  IActiveCombo,
  'totalDiscount' | 'totalAmount' | 'discountByCombo'
>;

// only used in Combo
// suggest move combo modules later
export const getComboPromotionDetail = (
  promotion: DiscoveryPromotion,
  latestPrice: number,
): ComboPromotionView | undefined => {
  const items: Item[] = [];
  const benefit = promotion.benefit;
  if (benefit.discount) {
    const { flat, percent, maxAmount } = benefit.discount;
    let discount = 0;
    if (percent) {
      discount = (latestPrice * percent) / 100;
      if (discount > maxAmount) {
        discount = maxAmount;
      }
    }
    if (flat) {
      discount = flat;
    }
    items.push({
      icon: 'cash',
      image: null,
      name: null,
      discount,
    });
  }
  if (benefit.voucher) {
    items.push({
      icon: 'voucher',
      image: null,
      name: benefit.voucher.name,
      discount: null,
    });
  }
  benefit.gifts.forEach((el) => {
    items.push({
      icon: 'gift',
      image: el.imageUrl,
      name: el.name,
      discount: null,
    });
  });
  if (items.length > 1) {
    return {
      hasImage: false,
      icon: 'gift',
      tag: t('Tặng ngay'),
      description: t('{{ItemsLength}} phần quà', { ItemsLength: items.length }),
    };
  }
  if (items.length === 1) {
    const item = items[0];
    return {
      hasImage: !!item.image,
      image: item.image || undefined,
      icon: item.image ? undefined : item.icon,
      tag: item.icon === 'cash' ? t('Giảm ngay') : t('Tặng ngay'),
      discount: item.discount || undefined,
      title: item.discount ? undefined : item.name ?? undefined,
    };
  }
};

export const getTotalAmountBeforePromotion = (
  combo: ICombo,
  isApplyNewLogic: boolean,
) => {
  const field: keyof IProductPrice =
    isApplyNewLogic || !combo.promotion.condition?.exclusions?.length
      ? 'latestPrice'
      : 'supplierRetailPrice';
  return combo.conditions.reduce((total, currentValue) => {
    return (
      total +
      (currentValue.product.price[field] || 0) * currentValue.requiredQuantity
    );
  }, 0);
};

export const getTotalAmount = (combo: ICombo, isApplyNewLogic: boolean) => {
  return (
    getTotalAmountBeforePromotion(combo, isApplyNewLogic) -
    getDiscountByCombo(combo, isApplyNewLogic)
  );
};

export const getDiscountByCombo = (combo: ICombo, isApplyNewLogic: boolean) => {
  const { flat, percent, maxAmount } = combo.promotion.benefit?.discount || {};
  if (flat) return flat;

  if (percent) {
    const totalAmount = getTotalAmountBeforePromotion(combo, isApplyNewLogic);
    if (typeof maxAmount === 'number')
      return Math.min((percent * totalAmount) / 100, maxAmount);
    return (percent * totalAmount) / 100;
  }

  return 0;
};

export const getTotalDiscount = (combo: ICombo, isApplyNewLogic: boolean) => {
  let totalDiscount = getDiscountByCombo(combo, isApplyNewLogic);
  // Promotion is applied with other promotions (combo.promotion.condition?.exclusions.length = null)
  if (
    isApplyNewLogic ||
    (!isApplyNewLogic && !combo.promotion.condition?.exclusions?.length)
  ) {
    const totalProductDiscount = combo.conditions.reduce((total, curr) => {
      return (
        total +
        (curr.product.price.supplierRetailPrice -
          curr.product.price.latestPrice) *
          curr.requiredQuantity
      );
    }, 0);
    totalDiscount += totalProductDiscount;
  }
  return totalDiscount;
};

export const getNewComboData = (
  selectingProduct: ISelectingProduct,
  comboGroup: IComboGroup,
  activeCombo: IActiveCombo,
  isApplyNewLogic: boolean,
) => {
  const { totalProductInCombo, comboKey, conditionKey, product } =
    selectingProduct;
  const selectedGroup = comboGroup[totalProductInCombo];
  const selectedCombo = selectedGroup.combos.find(
    (combo) => combo.key === comboKey,
  ) || {
    ...activeCombo,
  };

  const unaggregatedData = {
    ...selectedCombo,
    conditions: selectedCombo.conditions.map((c) =>
      c.key === conditionKey ? { ...c, product } : c,
    ),
  };
  const newActiveCombo: IActiveCombo = {
    ...unaggregatedData,
    discountByCombo: getDiscountByCombo(unaggregatedData, isApplyNewLogic),
    totalDiscount: getTotalDiscount(unaggregatedData, isApplyNewLogic),
    totalAmount: getTotalAmount(unaggregatedData, isApplyNewLogic),
  };
  const newGroup: IComboGroup = {
    ...comboGroup,
    [totalProductInCombo]: {
      combos: selectedGroup.combos.map((cb) =>
        cb.key === comboKey ? newActiveCombo : cb,
      ),
    },
  };

  return { newActiveCombo, newGroup };
};

export const countAndGetBenefitDescription = (
  benefit: IPromotionBenefit,
  numberDisplayFormat?: NumberDisplayFormat,
) => {
  let count = 0;
  let description = '';
  const benefits = [];
  const { discount, voucher, gifts } = benefit;
  const discountValue = discount?.flat || discount?.percent || 0;
  if (discount && discountValue > 0) {
    count++;
    benefits.push(
      `${t('Giảm ngay')} ${
        discount?.flat
          ? formatCurrency(discountValue, numberDisplayFormat)
          : `${discountValue}%`
      }`,
    );
  }
  if (voucher) {
    count++;
    benefits.push(
      voucher.quantity > 1
        ? `${voucher.quantity} ${voucher.name}`
        : voucher.name,
    );
  }
  if (gifts && !!gifts.length) {
    gifts.forEach(({ name, quantity = 0 }) => {
      count++;
      benefits.push(quantity > 1 ? `${quantity} ${name}` : name);
    });
  }
  benefits.forEach((benefit, index) => {
    description +=
      index === 0
        ? benefit
        : index < benefits.length - 1
        ? `, ${benefit}`
        : ` ${t('và')} ${benefit}`;
  });

  return { numberOfBenefit: count, benefitDescription: description };
};

export const mapComboWithKey = (products: IComboProduct[]) =>
  products.map((p) =>
    p.combos.map((cb, key) => ({
      ...cb,
      key: key + 1,
      conditions: cb.conditions.map((c, i) => ({ ...c, key: i + 1 })),
    })),
  );

export const calculateProductComboQuantity = (items: CartItem[]) => {
  return items.reduce((quantity, item) => {
    return quantity + item.quantity;
  }, 0);
};

export const getSkusForProductsCombos = (items: CartItemGroup[]) => {
  const skus: string[] = [];
  items.forEach(({ items }) => {
    items.forEach((i) => {
      if (!skus.includes(i.sku)) {
        skus.push(i.sku);
      }
    });
  });
  return skus;
};
